'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSearchParams, useRouter } from 'next/navigation';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/Toast';
import { useAnalytics } from '@/components/providers/AnalyticsProvider';
import { createClient } from '@/lib/supabase/browser';

import FormInput from '@/components/ui/FormInput';
import { Mail, Lock, ArrowRight } from 'lucide-react';
import { googleAuthEnabled } from '@/lib/flags';

const TESTIMONIALS = [
  {
    quote: "Indiestry helped me land my first video game role within two weeks of joining. The audition process is seamless.",
    name: "Kailynn H.",
    role: "Voice Actor",
    initial: "K",
  },
  {
    quote: "Finally a platform built specifically for voice actors. I can showcase my demo reel and apply to roles all in one place.",
    name: "MiaFaeVa",
    role: "Animation Voice Actor",
    initial: "M",
  },
  {
    quote: "The community here actually understands what voice acting is. I've made more industry connections here than anywhere else.",
    name: "Reverbeon",
    role: "Voice Actor & Narrator",
    initial: "R",
  },
  {
    quote: "Posting my first job casting call was so easy. I had 12 auditions submitted within 24 hours.",
    name: "Batman",
    role: "Indie Game Developer",
    initial: "B",
  },
  {
    quote: "As someone new to voice acting, Indiestry made it easy to find real opportunities and build my portfolio.",
    name: "coyotecyb",
    role: "Emerging Voice Actor",
    initial: "C",
  },
]

export default function SignupContent() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [userPath, setUserPath] = useState<string>('');
  const [remember, setRemember] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string; confirmPassword?: string }>({});
  const [isClient, setIsClient] = useState(false);
  const [memberCount, setMemberCount] = useState(0);
  const [jobCount, setJobCount] = useState(0);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  const searchParams = useSearchParams();
  const router = useRouter();
  const { showToast } = useToast();
  const { trackEvent } = useAnalytics();

  useEffect(() => { setIsClient(true); }, []);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const supabase = createClient();
        const [{ count: members }, { count: jobs }] = await Promise.all([
          supabase.from('profiles').select('id', { count: 'exact', head: true }),
          supabase.from('job_postings').select('id', { count: 'exact', head: true }).eq('status', 'published'),
        ]);
        setMemberCount(members ?? 0);
        setJobCount(jobs ?? 0);
      } catch {
        // Stats are optional — silently ignore
      }
    };
    loadStats();
  }, []);

  useEffect(() => {
    const path = searchParams.get('path');
    if (path) setUserPath(path);
  }, [searchParams]);

  // account-type selection removed — all signups go directly to talent flow

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setTestimonialIndex(prev => (prev + 1) % TESTIMONIALS.length);
        setFadeIn(true);
      }, 400);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const testimonial = TESTIMONIALS[testimonialIndex];

  // Mirrors the FormInput password checklist + Supabase's four-class policy.
  // Keep these in lockstep — the API will reject with 400 if any class is
  // missing.
  const passwordRequirementsMet =
    password.length >= 10 &&
    /[A-Z]/.test(password) &&
    /\d/.test(password) &&
    /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

  const validateForm = () => {
    const newErrors: { email?: string; password?: string; confirmPassword?: string } = {};
    if (!email) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = 'Please enter a valid email address';
    if (!password) newErrors.password = 'Password is required';
    else if (password.length < 10) newErrors.password = 'Password must be at least 10 characters long';
    else if (!/[A-Z]/.test(password)) newErrors.password = 'Password must contain at least 1 uppercase letter';
    else if (!/\d/.test(password)) newErrors.password = 'Password must contain at least 1 number';
    else if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) newErrors.password = 'Password must contain at least 1 symbol';
    if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setErrors({});

    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          display_name: null,
          username: null,
          full_name: null,
        }),
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.error || 'Sign-up failed');
      }

      // Don't toast — the dedicated /auth/check-email page is the
      // success surface now (clearer affordance than a 5-second toast).
      trackEvent('signup_attempt', { method: 'email' });

      if (typeof window !== 'undefined') {
        try { sessionStorage.setItem('lastSignupEmail', email); } catch {}
      }

      router.push(`/auth/check-email?email=${encodeURIComponent(email)}&fresh=1`);

    } catch (error: any) {
      // Default to whatever the API said. Fall back to the generic copy only
      // if the body was empty/malformed — previously every failure showed
      // the generic line, which masked actionable Supabase errors.
      const apiMessage =
        typeof error?.message === 'string' && error.message.trim()
          ? error.message
          : 'An error occurred during sign up.';
      let errorMessage = apiMessage;
      if (apiMessage.includes('User already registered') || apiMessage.includes('already been registered')) {
        errorMessage = 'You already have an account. Try logging in or reset your password.';
        setErrors({ email: 'This email is already registered. Please log in or reset your password.' });
      } else if (apiMessage.includes('Invalid email')) {
        errorMessage = 'Please enter a valid email address.';
        setErrors({ email: 'Please enter a valid email address' });
      } else if (apiMessage.includes('Password should be at least')) {
        errorMessage = 'Password must be at least 10 characters long and include uppercase, a number, and a symbol.';
        setErrors({ password: 'Password must meet all requirements' });
      } else if (apiMessage.includes('Unable to validate email address')) {
        errorMessage = 'Please enter a valid email address.';
        setErrors({ email: 'Please enter a valid email address' });
      }
      showToast(errorMessage, 'error');
      trackEvent('signup_failure', { method: 'email', error: apiMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setIsGoogleLoading(true);
    try {
      if (!googleAuthEnabled) {
        showToast('Google signup is disabled. Please use email and password.', 'error');
        return;
      }

      const { signInWithGoogle } = await import('@/lib/auth-client');
      const { data, error } = await signInWithGoogle();

      if (error) throw error;

      trackEvent('signup_attempt', { method: 'google' });
      // The OAuth flow will redirect to /auth/callback, which handles the rest
    } catch (error: any) {
      console.error('Google signup error:', error);
      showToast(error?.message || 'An error occurred during Google sign up. Please try again.', 'error');
      trackEvent('signup_failure', { method: 'google', error: error?.message || 'Unknown error' });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  if (!isClient) {
    return (
      <div className="min-h-screen bg-surface-0 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">

      {/* Left panel — brand side.
          Layout was justify-between which spread Logo / Headline / Testimonial
          across the full panel height — on a 1080p viewport with browser
          chrome the testimonial sat below the fold. Switched to
          justify-start with explicit gaps so everything lives in the upper
          two-thirds and the testimonial is visible without scrolling. */}
      <div className="hidden lg:flex flex-col justify-start gap-10 p-12
        bg-surface-1 relative overflow-hidden">

        {/* Background decorative circles */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full
          bg-accent-soft blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full
          bg-accent-soft blur-3xl translate-y-1/2 -translate-x-1/2" />

        {/* Logo — single render, square aspect ratio explicit so the asset
            doesn't get stretched by the older width=140 prop. */}
        <div className="relative z-10">
          <Image
            src="/indiestrylogofull.png"
            alt="Indiestry"
            height={40}
            width={40}
            className="h-10 w-10"
            priority
          />
        </div>

        {/* Center content */}
        <div className="relative z-10">
          <h2 className="text-4xl font-bold text-text-primary leading-tight mb-3">
            Where voice actors<br />find their next role
          </h2>
          <p className="text-text-secondary text-lg leading-relaxed mb-6">
            Connect with casting directors, submit auditions, and build
            your professional voice acting career.
          </p>

          {/* Social proof stats */}
          <div className="flex items-center gap-8">
            {memberCount > 0 && (
              <div>
                <p className="text-2xl font-bold text-text-primary">{memberCount.toLocaleString()}</p>
                <p className="text-text-tertiary text-sm">Voice actors</p>
              </div>
            )}
            {jobCount > 0 && (
              <div>
                <p className="text-2xl font-bold text-text-primary">{jobCount.toLocaleString()}</p>
                <p className="text-text-tertiary text-sm">Active jobs</p>
              </div>
            )}
          </div>
        </div>

        {/* Rotating testimonial */}
        <div
          className="relative z-10 bg-surface-2 border border-border-subtle rounded-2xl p-6 transition-opacity duration-400"
          style={{ opacity: fadeIn ? 1 : 0 }}
        >
          <p className="text-text-secondary text-sm leading-relaxed italic mb-3">
            &ldquo;{testimonial.quote}&rdquo;
          </p>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-accent flex items-center
              justify-center text-text-on-accent text-xs font-bold flex-shrink-0">
              {testimonial.initial}
            </div>
            <div>
              <p className="text-text-primary text-sm font-medium">{testimonial.name}</p>
              <p className="text-text-tertiary text-xs">{testimonial.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 mt-4">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setFadeIn(false);
                  setTimeout(() => { setTestimonialIndex(i); setFadeIn(true); }, 400);
                }}
                className={`h-1.5 rounded-full transition-all ${
                  i === testimonialIndex ? 'w-4 bg-accent' : 'w-1.5 bg-border-strong'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right panel — form side */}
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-16
        bg-surface-0 min-h-screen lg:min-h-0">

        {/* Mobile logo */}
        <div className="lg:hidden mb-8">
          <Image src="/indiestrylogofull.png" alt="Indiestry" height={40} width={140}
            className="h-10 w-auto" />
        </div>

        <div className="max-w-sm w-full mx-auto lg:mx-0">
          <h1 className="text-2xl font-bold text-text-primary mb-1">Create your account</h1>
          <p className="text-text-secondary text-sm mb-8">Join Indiestry and start your voice acting journey</p>

          {/* Google Sign Up Button */}
          {googleAuthEnabled && (
            <>
              <button
                onClick={handleGoogleSignup}
                disabled={isGoogleLoading}
                className="w-full flex items-center justify-center gap-3 px-4 py-2.5
                  rounded-xl border border-border-default bg-surface-2 text-text-primary text-sm
                  font-medium hover:bg-surface-3 transition-colors mb-4 disabled:opacity-50"
              >
                {isGoogleLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 18 18">
                    <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
                    <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
                    <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18z"/>
                    <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
                  </svg>
                )}
                {isGoogleLoading ? 'Signing up...' : 'Continue with Google'}
              </button>
              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border-subtle" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-surface-0 px-3 text-xs text-text-tertiary">or sign up with email</span>
                </div>
              </div>
            </>
          )}

          {/* `relative isolate` creates a fresh stacking context for the
              form so any absolutely-positioned validation icon / eye toggle
              inside FormInput cannot ghost behind the input from a
              z-index inversion further up the tree. */}
          <form onSubmit={handleEmailSignup} className="space-y-4 relative isolate">
            <FormInput
              label="Email Address"
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={errors.email}
              icon={<Mail className="w-5 h-5 text-text-tertiary" />}
              placeholder="Enter your email"
              required
            />

            <FormInput
              label="Password"
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={errors.password}
              icon={<Lock className="w-5 h-5 text-text-tertiary" />}
              // Shortened from "Create a password (min. 10 characters)" so
              // the trailing eye toggle doesn't clip the closing paren on
              // narrow viewports — the requirements list below the input
              // already states the rules once the user starts typing.
              placeholder="Create a password"
              required
            />

            <FormInput
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              // Real-time mismatch hint: only show once the user has typed
              // something in the confirm field AND it actually differs from
              // password. The submit-time errors.confirmPassword still wins
              // when present — covers required/non-empty checks.
              error={
                errors.confirmPassword ||
                (confirmPassword.length > 0 && confirmPassword !== password
                  ? "Passwords don't match"
                  : undefined)
              }
              icon={<Lock className="w-5 h-5 text-text-tertiary" />}
              placeholder="Confirm your password"
              required
            />

            {/* Remember Me */}
            <div className="flex items-center gap-2.5">
              <input
                type="checkbox"
                id="remember"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="rounded border-border-default bg-surface-3 accent-accent cursor-pointer flex-shrink-0"
                style={{ width: '16px', height: '16px', minWidth: '16px' }}
              />
              <label htmlFor="remember" className="text-sm text-text-secondary cursor-pointer select-none">
                Stay signed in after closing the browser
              </label>
            </div>

            <button
              type="submit"
              disabled={
                isLoading ||
                !email ||
                !passwordRequirementsMet ||
                !confirmPassword ||
                password !== confirmPassword
              }
              className="w-full py-3 rounded-xl bg-accent
                text-text-on-accent font-medium text-sm hover:bg-accent-hover
                disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                <>
                  Create Account
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <p className="text-sm text-text-tertiary">
              Already have an account?{' '}
              <Link href="/login" className="text-accent hover:text-accent-hover font-medium">
                Sign in
              </Link>
            </p>
            <p className="text-sm text-text-tertiary">
              <Link href="/forgot-password" className="text-accent hover:text-accent-hover">
                Forgot your password?
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
